import CustomerViewer from 'models/customer_viewer';
import DtoConverter from 'scripts/application/dto_converters/customer_viewer_converter';
import { clearViewerExpiry, startViewerExpiry } from './lib/viewer_expiry_timers';
import { VIEWER_TTL_SECONDS } from 'scripts/adapters/gateways/customer_viewers_gateway';

/**
 * Keeps each loaded customer's `viewers` collection in step with the broker.
 *
 * There is one handler rather than the usual fetch/broadcast/delete set, because
 * retained messages collapse those into one event: the broker replays the current
 * records on subscribe, sends changes as they happen, and signals a departure by
 * clearing the record — which arrives as an empty payload on the same topic.
 */
export default class CustomerViewersGatewayObserver {
  constructor(context) {
    this.context = context;
  }

  get store() {
    return this.context.stores.customers;
  }

  onViewerChanged(viewerDto, { agentId, customerId, sessionId }, expiresInSeconds) {
    // Messages keep arriving for a customer we navigated away from until the
    // unsubscribe lands, and writing those would resurrect stores we just tore down.
    const viewersStore = this._viewersStore(customerId);
    if (!viewersStore) {
      return;
    }

    const viewerId = CustomerViewer.idFor(agentId, sessionId);

    // An empty payload is the broker clearing a retained record — that tab left, or
    // its record expired. It says nothing about the agent's other tabs, which own
    // their own records.
    if (!viewerDto) {
      clearViewerExpiry(customerId, viewerId);
      viewersStore.remove(viewerId);

      return;
    }

    const viewer = DtoConverter.fromDto(viewerDto, { agentId, sessionId });
    if (!this._makeRoomFor(viewersStore, viewer, customerId)) {
      return;
    }

    viewersStore.addOrReplace(viewer);

    // The broker's remaining expiry where it gave one, so a record replayed 44
    // seconds into its life is held for the second it has left rather than another
    // full TTL. Falls back to the full TTL on a live publish, which carries none.
    const ttlSeconds = expiresInSeconds || VIEWER_TTL_SECONDS;
    startViewerExpiry(customerId, viewerId, ttlSeconds, () => this._viewersStore(customerId));
  }

  /**
   * Whether the record can be held, evicting one of the agent's own to make room.
   *
   * The broker binds only the agent segment of the topic to the publisher, so the
   * session segment is a client-minted string it does not constrain: one agent can
   * leave arbitrarily many records on one customer. Each costs every other agent
   * viewing that customer a model, a live timer, and a pass through the pill's
   * per-change reduction — none of which would show up on screen, because the pill
   * renders one entry per agent however many records back it.
   */
  _makeRoomFor(viewersStore, viewer, customerId) {
    const held = viewersStore.findAll({ agentId: viewer.agentId });
    if (held.length < MAX_RECORDS_PER_AGENT || held.some(record => record.id === viewer.id)) {
      return true;
    }

    // The pill shows an agent's earliest arrival, so the latest-arriving record is
    // the one whose loss cannot change what anyone sees.
    const latest = held.reduce((a, b) => (arrivalTime(b.arrivedAt) > arrivalTime(a.arrivedAt) ? b : a));
    if (arrivalTime(viewer.arrivedAt) >= arrivalTime(latest.arrivedAt)) {
      return false;
    }

    clearViewerExpiry(customerId, latest.id);
    viewersStore.remove(latest.id);

    return true;
  }

  _viewersStore(customerId) {
    if (!customerId || !this.store.has({ id: customerId })) {
      return null;
    }

    return this.store.storesFor(customerId).viewers;
  }
}

// An absent or unparseable arrival sorts last rather than poisoning the comparison
// with NaN, which makes it the first record evicted.
function arrivalTime(arrivedAt) {
  const time = arrivedAt ? new Date(arrivedAt).getTime() : NaN;

  return Number.isFinite(time) ? time : Infinity;
}

// Comfortably above the number of tabs an agent opens on one customer, low enough
// that a client minting session ids in a loop cannot grow every other agent's store.
const MAX_RECORDS_PER_AGENT = 5;
